import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function PostComments({ postId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/posts/${postId}/comments`);
      setComments(data);
    } catch {}
  };

  useEffect(() => { load(); }, [postId]);

  // Flatten the thread into an ordered list with a depth marker so replies (and
  // replies-to-replies, Instagram-style) render nested without a recursive
  // component.
  const ordered = useMemo(() => {
    const byParent = {};
    const ids = new Set(comments.map((c) => c.id));
    const roots = [];
    comments.forEach((c) => {
      const pid = c.parent_id && ids.has(c.parent_id) ? c.parent_id : null;
      if (pid) (byParent[pid] = byParent[pid] || []).push(c);
      else roots.push(c);
    });
    const out = [];
    const stack = roots.slice().reverse().map((c) => ({ c, depth: 0 }));
    while (stack.length) {
      const { c, depth } = stack.pop();
      out.push({ ...c, _depth: Math.min(depth, 4) });
      const kids = byParent[c.id] || [];
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ c: kids[i], depth: depth + 1 });
    }
    return out;
  }, [comments]);

  const submit = async (e, parentId = null) => {
    e?.preventDefault?.();
    if (!content.trim()) return;
    if (!user) { toast.error("Log in to comment"); return; }
    setPosting(true);
    try {
      await api.post(`/posts/${postId}/comments`, { content, parent_id: parentId });
      setContent("");
      setReplyTo(null);
      await load();
    } catch { toast.error("Failed to comment"); }
    finally { setPosting(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid={`comments-${postId}`}>
      {user && (
        <form onSubmit={(e) => submit(e, null)} className="flex items-center gap-2 mb-4">
          <img src={(user.avatar_url || user.avatar)} alt="" className="w-8 h-8 rounded-full" />
          <input
            value={replyTo ? "" : content}
            onChange={(e) => setContent(e.target.value)}
            onFocus={() => setReplyTo(null)}
            placeholder="Write a comment…"
            className="flex-1 h-10 px-4 rounded-full bg-white/5 border border-white/10 focus:outline-none focus:border-white/30 text-sm"
            data-testid={`comment-input-${postId}`}
          />
          <Button
            type="submit"
            disabled={posting || !!replyTo || !content.trim()}
            className="h-10 w-10 p-0 rounded-full text-black"
            style={{ background: "linear-gradient(135deg,#FFBF00,#FF8A00)" }}
            data-testid={`comment-send-${postId}`}
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      )}
      <div className="space-y-3">
        {ordered.map((c) => {
          const isReplying = replyTo === c.id;
          return (
            <div
              key={c.id}
              className={c._depth > 0 ? "border-l border-white/10" : ""}
              style={{ marginLeft: c._depth * 22, paddingLeft: c._depth ? 12 : 0 }}
              data-testid={`comment-${c.id}`}
            >
              <div className="flex items-start gap-2.5">
                {c.author && <img src={c.author.avatar_url} alt="" className="w-8 h-8 rounded-full mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="bg-white/5 border border-white/10 rounded-2xl px-3 py-2">
                    <div className="text-xs text-white/60">
                      <span className="font-semibold text-white/90">{c.author?.name}</span>
                      <span className="ml-2">@{c.author?.username}</span>
                      <span className="ml-2 text-white/35">· {timeAgo(c.created_at)}</span>
                    </div>
                    <div className="text-sm text-white/85 whitespace-pre-wrap break-words">{c.content}</div>
                  </div>
                  {user && (
                    <button
                      onClick={() => { setReplyTo(isReplying ? null : c.id); setContent(""); }}
                      className="text-[11px] text-white/45 hover:text-[#9387FE] mt-1 ml-3 font-medium"
                      data-testid={`comment-reply-btn-${c.id}`}
                    >
                      {isReplying ? "Cancel" : "Reply"}
                    </button>
                  )}
                  {isReplying && (
                    <form onSubmit={(e) => submit(e, c.id)} className="flex items-center gap-2 mt-2">
                      <input
                        autoFocus
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder={`Reply to @${c.author?.username || "user"}…`}
                        className="flex-1 h-9 px-3 rounded-full bg-white/5 border border-white/10 focus:outline-none focus:border-white/30 text-sm"
                        data-testid={`comment-reply-input-${c.id}`}
                      />
                      <Button type="submit" disabled={posting || !content.trim()} className="h-9 w-9 p-0 rounded-full text-black" style={{ background: "linear-gradient(135deg,#FFBF00,#FF8A00)" }} data-testid={`comment-reply-send-${c.id}`}>
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {comments.length === 0 && <div className="text-xs text-white/40 text-center py-2">No comments yet.</div>}
      </div>
    </div>
  );
}
